import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { DegapCycle } from '@/types/prioritization/degapCycle';
import type { CatchballParticipantRef } from '@/types/prioritization/catchballVirtualConfig';
import type {
  CycleDesignerFormState,
  CycleDesignerType,
  DesignerPath,
  DesignerStep,
  OrgPathNode,
  PathSlotId,
  TrackParams,
} from '../types';
import {
  DEFAULT_TRACK_PARAMS,
  PATH_COLORS,
  pathSlotIdForIndex,
  pathColorForIndex,
} from '../types';
import { buildOrgById, canAddToPath, copyAssignmentFromPaths, setOwnerOnUnit, toggleNodeOnPath } from '../lib/pathBuilder';
import { readDesignerDraft } from '../lib/designerDraft';
import {
  appendCustomRung,
  assignCustomRung,
  moveCustomRung,
  CUSTOM_ANCHOR_ID,
  pathModeOf,
  removeCustomRung,
} from '../lib/customPathModel';
import type { PathMode } from '../types';

function readMetadataString(cycle: DegapCycle, key: string): string {
  if (!cycle.metadata || typeof cycle.metadata !== 'object') return '';
  const v = (cycle.metadata as Record<string, unknown>)[key];
  return typeof v === 'string' ? v : '';
}

function readMetadataNumber(cycle: DegapCycle, key: string, fallback: number): number {
  if (!cycle.metadata || typeof cycle.metadata !== 'object') return fallback;
  const v = (cycle.metadata as Record<string, unknown>)[key];
  return typeof v === 'number' ? v : fallback;
}

function formatDateInput(d: Date | undefined): string {
  if (!d) return '';
  const iso = d instanceof Date ? d.toISOString() : String(d);
  return iso.slice(0, 10);
}

export function initialFormFromCycle(cycle: DegapCycle): CycleDesignerFormState {
  const dateRange = (cycle.metadata as Record<string, unknown> | undefined)?.designerDateRange;
  let startDate = '';
  let endDate = '';
  if (dateRange && typeof dateRange === 'object') {
    const dr = dateRange as Record<string, unknown>;
    if (typeof dr.start === 'string') startDate = dr.start;
    if (typeof dr.end === 'string') endDate = dr.end;
  }
  if (!startDate) startDate = formatDateInput(cycle.startedAt ?? cycle.createdAt);
  if (!endDate) endDate = formatDateInput(cycle.cadence?.targetEndDate);

  const cycleTypeRaw = readMetadataString(cycle, 'cycleDesignerType');
  const cycleType: CycleDesignerType =
    cycleTypeRaw === 'expedited' || cycleTypeRaw === 'discovery' ? cycleTypeRaw : 'standard';

  // Written at launch, so reopening a launched design (whose draft was
  // stripped) restores the mode its stops were drawn in.
  const pathMode: PathMode =
    readMetadataString(cycle, 'designerPathMode') === 'custom' ? 'custom' : 'org_chart';

  return {
    cycleName: cycle.name,
    strategicIntent: readMetadataString(cycle, 'strategicIntent') || cycle.description || '',
    startDate,
    endDate,
    cycleType,
    // 0 = no limit. Owner ruling 2026-07-08: never impose a max-iterations cap.
    maxIterations: readMetadataNumber(cycle, 'maxIterations', 0),
    pathMode,
  };
}

export function initialPaths(anchorId: string): DesignerPath[] {
  return [
    {
      id: 'a',
      // Canon: the thing being designed here is a PATH (the route the ball
      // travels). A Track is the container the launch creates from it — so the
      // designer must never seed its rows with Track wording.
      name: 'Path A',
      color: PATH_COLORS.a,
      nodes: [anchorId],
      params: { ...DEFAULT_TRACK_PARAMS },
    },
  ];
}

export function useCycleDesignerState(cycle: DegapCycle | undefined, anchorId: string | undefined) {
  const [step, setStep] = useState<DesignerStep>(() => readDesignerDraft(cycle)?.step ?? 1);
  const [form, setForm] = useState<CycleDesignerFormState>(() => {
    const draft = readDesignerDraft(cycle);
    if (draft) return draft.form;
    return cycle ? initialFormFromCycle(cycle) : {
      cycleName: '',
      strategicIntent: '',
      startDate: '',
      endDate: '',
      cycleType: 'standard',
      maxIterations: 0,
    };
  });
  const [paths, setPaths] = useState<DesignerPath[]>(() => {
    const draft = readDesignerDraft(cycle);
    if (draft) return draft.paths;
    return anchorId ? initialPaths(anchorId) : [];
  });
  const [activePathId, setActivePathId] = useState<PathSlotId>('a');
  const [rightTab, setRightTab] = useState<'flows' | 'json'>('flows');
  const [toast, setToast] = useState<string | null>(null);

  // Stores hydrate async: on a refresh the cycle is often undefined at first
  // render, so the useState initializers above ran with nothing. Re-hydrate
  // once when the cycle arrives (or changes identity) — guarded by id so the
  // autosave's own metadata writes don't re-trigger and stomp live edits.
  const hydratedCycleIdRef = useRef<string | null>(cycle?.id ?? null);
  useEffect(() => {
    if (!cycle || hydratedCycleIdRef.current === cycle.id) return;
    hydratedCycleIdRef.current = cycle.id;
    const draft = readDesignerDraft(cycle);
    setForm(draft?.form ?? initialFormFromCycle(cycle));
    if (draft?.paths.length) {
      setPaths(draft.paths);
    } else if (anchorId) {
      setPaths(initialPaths(anchorId));
    }
    if (draft) setStep(draft.step);
  }, [cycle, anchorId]);

  /**
   * Level 0 of every path. Custom paths anchor on the cycle owner rather than
   * an org unit, so the mode decides the anchor — and changing the mode
   * therefore rebuilds the paths through the effect below.
   */
  const effectiveAnchorId = pathModeOf(form) === 'custom' ? CUSTOM_ANCHOR_ID : anchorId ?? '';

  useEffect(() => {
    if (!effectiveAnchorId) return;
    const anchorId = effectiveAnchorId;
    setPaths((prev) => {
      if (prev.length === 0) return initialPaths(anchorId);
      const needsUpdate = prev.some((p) => !p.nodes.includes(anchorId));
      if (!needsUpdate) return prev;
      // Also the path-mode switch: the effective anchor changes, so stops built
      // against the old anchor are meaningless. Drop their assignments and
      // windows too, or they linger invisibly keyed to removed stops.
      return prev.map((p) =>
        p.nodes.includes(anchorId)
          ? p
          : { ...p, nodes: [anchorId], nodeAssignments: {}, nodeWindows: {} },
      );
    });
  }, [effectiveAnchorId]);

  const activePath = useMemo(
    () => paths.find((p) => p.id === activePathId),
    [paths, activePathId],
  );

  const showToast = useCallback((message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(null), 2200);
  }, []);

  const patchForm = useCallback((patch: Partial<CycleDesignerFormState>) => {
    setForm((prev) => ({ ...prev, ...patch }));
  }, []);

  /**
   * A click that would put a second stop in the same row of a path. Forking is
   * allowed (owner ruling 2026-10-13), but one stray click used to fork the
   * path silently, and every act after it no longer said which branch it meant.
   * So the click asks: branch this path, or start a new path to that unit.
   */
  const [pendingFork, setPendingFork] = useState<{ nodeId: string; pathId: PathSlotId } | null>(null);

  const addNodeToPath = useCallback(
    (pathId: PathSlotId, nodeId: string, orgById: Map<string, OrgPathNode>) => {
      setPaths((prev) =>
        prev.map((p) => {
          if (p.id !== pathId) return p;
          const next = toggleNodeOnPath(nodeId, p, orgById);
          if (!next.nodes.includes(nodeId)) return next;
          return copyAssignmentFromPaths(next, nodeId, prev);
        }),
      );
    },
    [],
  );

  const toggleNode = useCallback(
    (nodeId: string, orgById: Map<string, OrgPathNode>) => {
      const active = paths.find((p) => p.id === activePathId);
      if (active) {
        const { action, reason } = canAddToPath(nodeId, active, orgById);
        if (action === 'add' && reason.includes('forks')) {
          setPendingFork({ nodeId, pathId: active.id });
          return;
        }
      }
      setPendingFork(null);
      addNodeToPath(activePathId, nodeId, orgById);
    },
    [activePathId, paths, addNodeToPath],
  );

  /** Answer the fork question: branch the path it was asked on. */
  const confirmFork = useCallback(
    (orgById: Map<string, OrgPathNode>) => {
      if (!pendingFork) return;
      const path = paths.find((p) => p.id === pendingFork.pathId);
      const parent = orgById.get(orgById.get(pendingFork.nodeId)?.parentId ?? '');
      addNodeToPath(pendingFork.pathId, pendingFork.nodeId, orgById);
      if (path) showToast(`${path.name} now branches below ${parent?.name ?? 'the stop above'}`);
      setPendingFork(null);
    },
    [pendingFork, paths, addNodeToPath, showToast],
  );

  /** Answer the fork question: a new path from the anchor down to that unit. */
  const forkAsNewPath = useCallback(
    (orgById: Map<string, OrgPathNode>) => {
      if (!pendingFork) return;
      const chain: string[] = [];
      let cursor: string | null = pendingFork.nodeId;
      while (cursor) {
        chain.unshift(cursor);
        if (orgById.get(cursor)?.relativeLevel === 0) break;
        cursor = orgById.get(cursor)?.parentId ?? null;
      }
      const used = new Set(paths.map((p) => p.id));
      let index = 0;
      while (used.has(pathSlotIdForIndex(index))) index += 1;
      const nextId = pathSlotIdForIndex(index);
      const base: DesignerPath = {
        id: nextId,
        name: `Path ${nextId.toUpperCase()}`,
        color: pathColorForIndex(index),
        nodes: chain,
        params: { ...DEFAULT_TRACK_PARAMS },
      };
      setPaths((prev) => {
        let made = base;
        for (const nodeId of chain) made = copyAssignmentFromPaths(made, nodeId, prev);
        return [...prev, made];
      });
      setActivePathId(nextId);
      setPendingFork(null);
      showToast(`Started ${base.name} to ${orgById.get(pendingFork.nodeId)?.name ?? 'that unit'}`);
    },
    [pendingFork, paths, showToast],
  );

  const cancelFork = useCallback(() => setPendingFork(null), []);

  const addPath = useCallback(
    (anchorId: string) => {
      // No cap: walk indices until an unused slot turns up. The old code took the
      // first free entry of a fixed six-item list and refused when it ran out.
      const used = new Set(paths.map((p) => p.id));
      let index = 0;
      while (used.has(pathSlotIdForIndex(index))) index += 1;
      const nextId = pathSlotIdForIndex(index);
      const newPath: DesignerPath = {
        id: nextId,
        name: `Path ${nextId.toUpperCase()}`,
        color: pathColorForIndex(index),
        nodes: [anchorId],
        params: { ...DEFAULT_TRACK_PARAMS },
      };
      setPaths((prev) => [...prev, newPath]);
      setActivePathId(nextId);
      showToast(`Created ${newPath.name}`);
    },
    [paths, showToast],
  );

  const removePath = useCallback(
    (pathId: PathSlotId) => {
      if (paths.length <= 1) {
        showToast('At least one path is required');
        return;
      }
      const removed = paths.find((p) => p.id === pathId);
      setPaths((prev) => prev.filter((p) => p.id !== pathId));
      if (activePathId === pathId) {
        setActivePathId(paths.find((p) => p.id !== pathId)!.id);
      }
      if (removed) showToast(`Removed ${removed.name}`);
    },
    [paths, activePathId, showToast],
  );

  const renamePath = useCallback((pathId: PathSlotId, name: string) => {
    setPaths((prev) => prev.map((p) => (p.id === pathId ? { ...p, name } : p)));
  }, []);

  const updateParam = useCallback(
    (pathId: PathSlotId, key: keyof TrackParams, value: TrackParams[keyof TrackParams]) => {
      setPaths((prev) =>
        prev.map((p) =>
          p.id === pathId ? { ...p, params: { ...p.params, [key]: value } } : p,
        ),
      );
    },
    [],
  );

  /** Set (or clear, with days=null) a per-node response-window override. */
  const setNodeWindow = useCallback(
    (pathId: PathSlotId, nodeId: string, days: number | null) => {
      setPaths((prev) =>
        prev.map((p) => {
          if (p.id !== pathId) return p;
          const next = { ...(p.nodeWindows ?? {}) };
          if (days !== null && Number.isFinite(days) && days > 0) next[nodeId] = Math.round(days);
          else delete next[nodeId];
          return { ...p, nodeWindows: next };
        }),
      );
    },
    [],
  );

  /**
   * Name (or clear, with ref=null) a unit's owner. The owner belongs to the
   * unit for the whole cycle, so it is set on every path through it — picking
   * it on one path left the others blank, and switching paths read as the
   * owner being lost.
   */
  const setNodeAssignment = useCallback((nodeId: string, ref: CatchballParticipantRef | null) => {
    setPaths((prev) => setOwnerOnUnit(prev, nodeId, ref));
  }, []);

  /**
   * Switch the cycle between org-chart stops and named recipients. The paths
   * themselves reset via the anchor effect above — the effective anchor changes
   * with the mode, and stops drawn against the old anchor cannot survive it.
   */
  const setPathMode = useCallback((mode: PathMode) => {
    setForm((prev) => (pathModeOf(prev) === mode ? prev : { ...prev, pathMode: mode }));
  }, []);

  /** Append a named recipient to the bottom of the active custom path. */
  const addCustomRung = useCallback(
    (ref: CatchballParticipantRef) => {
      setPaths((prev) => prev.map((p) => (p.id === activePathId ? appendCustomRung(p, ref) : p)));
    },
    [activePathId],
  );

  /** Point one stop on the active custom path at a different user or group. */
  const assignCustomRungRef = useCallback(
    (nodeId: string, ref: CatchballParticipantRef) => {
      setPaths((prev) =>
        prev.map((p) => (p.id === activePathId ? assignCustomRung(p, nodeId, ref) : p)),
      );
    },
    [activePathId],
  );

  const removeCustomRungFromPath = useCallback(
    (nodeId: string) => {
      setPaths((prev) => prev.map((p) => (p.id === activePathId ? removeCustomRung(p, nodeId) : p)));
    },
    [activePathId],
  );

  const moveCustomRungOnPath = useCallback(
    (nodeId: string, direction: 'up' | 'down') => {
      setPaths((prev) =>
        prev.map((p) => (p.id === activePathId ? moveCustomRung(p, nodeId, direction) : p)),
      );
    },
    [activePathId],
  );

  return {
    step,
    setStep,
    form,
    patchForm,
    effectiveAnchorId,
    setPathMode,
    addCustomRung,
    assignCustomRungRef,
    removeCustomRungFromPath,
    moveCustomRungOnPath,
    paths,
    setPaths,
    activePathId,
    setActivePathId,
    activePath,
    rightTab,
    setRightTab,
    toast,
    showToast,
    toggleNode,
    pendingFork,
    confirmFork,
    forkAsNewPath,
    cancelFork,
    addPath,
    removePath,
    renamePath,
    setNodeAssignment,
    setNodeWindow,
    updateParam,
    buildOrgById,
  };
}

export type CycleDesignerState = ReturnType<typeof useCycleDesignerState>;
